/**
 * UpkeepMonitoringTable Component
 * 
 * Table component for displaying Upkeep Budget Monitoring data
 * 3-level hierarchy: COA Group → COA → Activity
 * Each activity has 3 rows: UOM, HK, HK/UOM
 * 
 * Highlighting rules (cell-specific on Actual columns only):
 * - Orange: UOM row, when actual > 0 and budget = 0
 * - Yellow: HK/UOM row, when actual exceeds budget
 */

import React, { useMemo } from 'react';
import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    useBreakpointValue,
    VStack,
    HStack,
    Divider,
    Skeleton,
    Stack,
    Badge,
    Collapse,
    IconButton,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { UpkeepBudgetRow, UpkeepBudgetHierarchy } from '../types';
import { formatNumber, formatVariance, transformToHierarchy } from '../utils';

interface UpkeepMonitoringTableProps {
    data: UpkeepBudgetRow[];
    loading?: boolean;
}

/**
 * Get cell background color for highlighting (cell-specific)
 */
const getCellHighlightBg = (
    row: UpkeepBudgetRow,
    columnType: 'mtdActual' | 'ytdActual'
): string | undefined => {
    if (columnType === 'mtdActual') {
        if (row.highlightMtdActualOrange) return 'orange.200';
        if (row.highlightMtdActualYellow) return 'yellow.200';
    }
    if (columnType === 'ytdActual') {
        if (row.highlightYtdActualOrange) return 'orange.200';
        if (row.highlightYtdActualYellow) return 'yellow.200';
    }
    return undefined;
};

/**
 * Get color scheme for badge based on row type
 */
const getBadgeColorScheme = (rowType: string): string => {
    if (rowType === 'uom' || rowType === 'uom2') return 'blue';
    if (rowType === 'hk') return 'green';
    return 'purple';
};

/**
 * Get variance color for display
 */
const getVarianceColor = (variance: number | null): string => {
    if (variance === null) return 'gray.400';
    if (variance > 0) return 'red.600';
    if (variance < 0) return 'green.600';
    return 'gray.600';
};

/**
 * Generate unique key for row
 */
const getRowKey = (row: UpkeepBudgetRow): string => {
    const materialKey = row.rowType === 'material'
        ? `-${row.materialProductId ?? row.parameter}`
        : '';
    return `${row.coaGroup}-${row.coa}-${row.activity}-${row.rowType}${materialKey}`;
};

// ===========================================
// MOBILE VIEW COMPONENTS
// ===========================================

/**
 * Mobile Row Component for single parameter row
 */
const MobileRowItem: React.FC<{ row: UpkeepBudgetRow; isLast: boolean }> = ({ row, isLast }) => {
    const mtdActualHighlight = getCellHighlightBg(row, 'mtdActual');
    const ytdActualHighlight = getCellHighlightBg(row, 'ytdActual');

    return (
        <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
                {/* <Badge
                    colorScheme={getBadgeColorScheme(row.rowType)}
                    fontSize="xs"
                >
                    {row.parameter}
                </Badge> */}
                <Text fontSize="sm" fontWeight="bold" color="gray.700">
                    {row.parameter}
                </Text>
            </HStack>

            <HStack justify="space-between" fontSize="xs">
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">MTD Actual</Text>
                    <Box
                        bg={mtdActualHighlight}
                        px={mtdActualHighlight ? 1 : 0}
                        borderRadius="sm"
                    >
                        <Text fontWeight="semibold">{formatNumber(row.mtdActual)}</Text>
                    </Box>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">MTD Budget</Text>
                    <Text fontWeight="semibold">{formatNumber(row.mtdBudget)}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">Variance</Text>
                    <Text
                        fontWeight="semibold"
                        color={row.mtdVariance && row.mtdVariance > 0 ? 'red.500' : 'green.500'}
                    >
                        {formatVariance(row.mtdVariance)}
                    </Text>
                </VStack>
            </HStack>

            <HStack justify="space-between" fontSize="xs">
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">YTD Actual</Text>
                    <Box
                        bg={ytdActualHighlight}
                        px={ytdActualHighlight ? 1 : 0}
                        borderRadius="sm"
                    >
                        <Text fontWeight="semibold">{formatNumber(row.ytdActual)}</Text>
                    </Box>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">YTD Budget</Text>
                    <Text fontWeight="semibold">{formatNumber(row.ytdBudget)}</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                    <Text color="gray.500">Variance</Text>
                    <Text
                        fontWeight="semibold"
                        color={row.ytdVariance && row.ytdVariance > 0 ? 'red.500' : 'green.500'}
                    >
                        {formatVariance(row.ytdVariance)}
                    </Text>
                </VStack>
            </HStack>

            {!isLast && <Divider borderStyle="dashed" />}
        </VStack>
    );
};

/**
 * Mobile Activity Card - displays all rows for one activity
 */
const MobileActivityCard: React.FC<{
    activity: string;
    rows: UpkeepBudgetRow[];
}> = ({ activity, rows }) => {
    if (rows.length === 0) return null;

    return (
        <Box
            bg="white"
            p={3}
            borderRadius="md"
            border="1px"
            borderColor="gray.100"
        >
            <Text fontSize="xs" fontWeight="semibold" color="gray.700" mb={2}>
                {activity}
            </Text>
            <VStack align="stretch" spacing={2}>
                {rows.map((row, idx) => (
                    <MobileRowItem
                        key={getRowKey(row)}
                        row={row}
                        isLast={idx === rows.length - 1}
                    />
                ))}
            </VStack>
        </Box>
    );
};

/**
 * Mobile COA Card - groups activities under a COA
 */
const MobileCoaCard: React.FC<{
    coa: string;
    activities: { activity: string; rows: UpkeepBudgetRow[] }[];
}> = ({ coa, activities }) => {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
        <Box
            bg="gray.50"
            borderRadius="lg"
            border="1px"
            borderColor="gray.200"
            overflow="hidden"
        >
            <HStack
                p={3}
                cursor="pointer"
                onClick={() => setIsOpen(!isOpen)}
                bg="blue.50"
                _hover={{ bg: 'blue.100' }}
            >
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    size="xs"
                    variant="ghost"
                />
                <Text fontSize="sm" fontWeight="semibold" color="blue.700">
                    {coa}
                </Text>
                <Badge colorScheme="blue" fontSize="xs">
                    {activities.length} activity
                </Badge>
            </HStack>
            <Collapse in={isOpen}>
                <VStack align="stretch" spacing={2} p={3}>
                    {activities.map((act) => (
                        <MobileActivityCard
                            key={`${coa}-${act.activity}`}
                            activity={act.activity}
                            rows={act.rows}
                        />
                    ))}
                </VStack>
            </Collapse>
        </Box>
    );
};

/**
 * Mobile COA Group Card - top-level grouping
 */
const MobileCoaGroupCard: React.FC<{ group: UpkeepBudgetHierarchy }> = ({ group }) => {
    const [isOpen, setIsOpen] = React.useState(true);

    return (
        <Box
            bg="white"
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            overflow="hidden"
        >
            <HStack
                p={4}
                cursor="pointer"
                onClick={() => setIsOpen(!isOpen)}
                bg="customGreen.500"
                _hover={{ bg: 'customGreen.600' }}
            >
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
                    size="sm"
                    variant="ghost"
                    color="white"
                    _hover={{ bg: 'whiteAlpha.200' }}
                />
                <Text fontSize="md" fontWeight="bold" color="white" flex={1}>
                    {group.coaGroup}
                </Text>
                <Badge colorScheme="whiteAlpha" color="white" fontSize="xs">
                    {group.coaList.length} kategori
                </Badge>
            </HStack>
            <Collapse in={isOpen}>
                <VStack align="stretch" spacing={3} p={3}>
                    {group.coaList.map((coaItem) => (
                        <MobileCoaCard
                            key={`${group.coaGroup}-${coaItem.coa}`}
                            coa={coaItem.coa}
                            activities={coaItem.activities}
                        />
                    ))}
                </VStack>
            </Collapse>
        </Box>
    );
};

// ===========================================
// DESKTOP TABLE COMPONENTS
// ===========================================

/**
 * Desktop Table Header Component with COA Group, Category, and Activity columns
 */
const DesktopTableHeader: React.FC = () => (
    <Thead>
        <Tr bg="blue.700">
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
                minW="150px"
            >
                COA Group
            </Th>
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
                minW="180px"
            >
                Category
            </Th>
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
                minW="200px"
            >
                Activity
            </Th>
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
                minW="100px"
            >
                Parameter
            </Th>
            <Th
                colSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
            >
                MTD
            </Th>
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
                minW="80px"
            >
                Variance %
            </Th>
            <Th
                colSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                borderRight="1px"
                borderColor="blue.600"
                textAlign="center"
                py={3}
            >
                YTD
            </Th>
            <Th
                rowSpan={2}
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="bold"
                textAlign="center"
                py={3}
                minW="80px"
            >
                Variance %
            </Th>
        </Tr>
        <Tr bg="blue.700">
            <Th
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="semibold"
                borderRight="1px"
                borderColor="blue.500"
                textAlign="center"
                py={2}
            >
                Actual
            </Th>
            <Th
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="semibold"
                borderRight="1px"
                borderColor="blue.500"
                textAlign="center"
                py={2}
            >
                Budget
            </Th>
            <Th
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="semibold"
                borderRight="1px"
                borderColor="blue.500"
                textAlign="center"
                py={2}
            >
                Actual
            </Th>
            <Th
                color="white"
                fontSize="xs"
                textTransform="uppercase"
                fontWeight="semibold"
                borderRight="1px"
                borderColor="blue.500"
                textAlign="center"
                py={2}
            >
                Budget
            </Th>
        </Tr>
    </Thead>
);

/**
 * Desktop Data Row Component with rowSpan support and collapse buttons
 */
const DesktopDataRow: React.FC<{
    row: UpkeepBudgetRow;
    showCoaGroup: boolean;
    showCoa: boolean;
    showActivity: boolean;
    coaGroupRowSpan: number;
    coaRowSpan: number;
    activityRowSpan: number;
    isCoaGroupExpanded: boolean;
    isCoaExpanded: boolean;
    onToggleCoaGroup: () => void;
    onToggleCoa: () => void;
}> = ({
    row,
    showCoaGroup,
    showCoa,
    showActivity,
    coaGroupRowSpan,
    coaRowSpan,
    activityRowSpan,
    isCoaGroupExpanded,
    isCoaExpanded,
    onToggleCoaGroup,
    onToggleCoa
}) => {
        const mtdActualBg = getCellHighlightBg(row, 'mtdActual');
        const ytdActualBg = getCellHighlightBg(row, 'ytdActual');

        return (
            <Tr _hover={{ bg: 'gray.50' }}>
                {/* COA Group - only show on first row of group */}
                {showCoaGroup && (
                    <Td
                        rowSpan={coaGroupRowSpan}
                        fontSize="xs"
                        fontWeight="bold"
                        color="gray.700"
                        borderRight="1px"
                        borderColor="gray.200"
                        py={2}
                        px={2}
                        verticalAlign="top"
                    >
                        <HStack spacing={2}>
                            <IconButton
                                aria-label={isCoaGroupExpanded ? 'Collapse' : 'Expand'}
                                icon={isCoaGroupExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={onToggleCoaGroup}
                            />
                            <Text>{row.coaGroup}</Text>
                        </HStack>
                    </Td>
                )}

                {/* COA - only show on first row of COA */}
                {showCoa && (
                    <Td
                        rowSpan={coaRowSpan}
                        fontSize="xs"
                        fontWeight="semibold"
                        color="gray.700"
                        borderRight="1px"
                        borderColor="gray.200"
                        py={2}
                        px={2}
                        verticalAlign="top"
                    >
                        <HStack spacing={2}>
                            <IconButton
                                aria-label={isCoaExpanded ? 'Collapse' : 'Expand'}
                                icon={isCoaExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={onToggleCoa}
                            />
                            <Text>{row.coa}</Text>
                        </HStack>
                    </Td>
                )}

                {/* Activity - only show on first row of activity */}
                {showActivity && (
                    <Td
                        rowSpan={activityRowSpan}
                        fontSize="xs"
                        fontWeight="medium"
                        color="gray.700"
                        borderRight="1px"
                        borderColor="gray.200"
                        py={2}
                        verticalAlign="top"
                    >
                        {row.activity}
                    </Td>
                )}

                {/* Parameter */}
                <Td
                    fontSize="xs"
                    color={row.rowType === 'hk_per_uom' || row.rowType === 'hk_per_uom2' ? 'blue.600' : 'gray.600'}
                    fontWeight={row.rowType === 'hk_per_uom' || row.rowType === 'hk_per_uom2' ? 'semibold' : 'normal'}
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                >
                    {row.parameter}
                </Td>

                {/* MTD Actual */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                    bg={mtdActualBg}
                    fontWeight={mtdActualBg ? 'semibold' : 'normal'}
                >
                    {formatNumber(row.mtdActual)}
                </Td>

                {/* MTD Budget */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                >
                    {formatNumber(row.mtdBudget)}
                </Td>

                {/* MTD Variance */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    fontWeight="medium"
                    color={getVarianceColor(row.mtdVariance)}
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                >
                    {formatVariance(row.mtdVariance)}
                </Td>

                {/* YTD Actual */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                    bg={ytdActualBg}
                    fontWeight={ytdActualBg ? 'semibold' : 'normal'}
                >
                    {formatNumber(row.ytdActual)}
                </Td>

                {/* YTD Budget */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    borderRight="1px"
                    borderColor="gray.200"
                    py={1}
                >
                    {formatNumber(row.ytdBudget)}
                </Td>

                {/* YTD Variance */}
                <Td
                    fontSize="xs"
                    textAlign="right"
                    fontWeight="medium"
                    color={getVarianceColor(row.ytdVariance)}
                    py={1}
                >
                    {formatVariance(row.ytdVariance)}
                </Td>
            </Tr>
        );
    };

/**
 * Desktop Table Body Component with Collapse State Management
 */
const DesktopTableBody: React.FC<{ hierarchy: UpkeepBudgetHierarchy[] }> = ({ hierarchy }) => {
    // State for tracking expanded/collapsed groups and COAs
    const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>(() => {
        // Initialize all groups as expanded
        const initial: Record<string, boolean> = {};
        hierarchy.forEach(group => {
            initial[group.coaGroup] = true;
        });
        return initial;
    });

    const [expandedCoas, setExpandedCoas] = React.useState<Record<string, boolean>>(() => {
        // Initialize all COAs as expanded
        const initial: Record<string, boolean> = {};
        hierarchy.forEach(group => {
            group.coaList.forEach(coaItem => {
                const coaKey = `${group.coaGroup}::${coaItem.coa}`;
                initial[coaKey] = true;
            });
        });
        return initial;
    });

    const toggleCoaGroup = (coaGroup: string) => {
        setExpandedGroups(prev => ({
            ...prev,
            [coaGroup]: !prev[coaGroup]
        }));
    };

    const toggleCoa = (coaGroup: string, coa: string) => {
        const coaKey = `${coaGroup}::${coa}`;
        setExpandedCoas(prev => ({
            ...prev,
            [coaKey]: !prev[coaKey]
        }));
    };

    const calculateCoaRowCount = (activities: { activity: string; rows: UpkeepBudgetRow[] }[]): number => {
        return activities.reduce((sum, act) => sum + act.rows.length, 0);
    };

    const allRows: React.ReactNode[] = [];

    hierarchy.forEach((group) => {
        const isGroupExpanded = expandedGroups[group.coaGroup] ?? true;

        const visibleRowCount = group.coaList.reduce((sum, coaItem) => {
            const coaKey = `${group.coaGroup}::${coaItem.coa}`;
            const isCoaExpanded = expandedCoas[coaKey] ?? true;
            const coaRowCount = calculateCoaRowCount(coaItem.activities);
            return sum + (isCoaExpanded ? coaRowCount : 1);
        }, 0);
        let isFirstRowInGroup = true;

        group.coaList.forEach((coaItem) => {
            const coaKey = `${group.coaGroup}::${coaItem.coa}`;
            const isCoaExpanded = expandedCoas[coaKey] ?? true;
            const coaRowCount = calculateCoaRowCount(coaItem.activities);
            let isFirstRowInCoa = true;

            coaItem.activities.forEach((activityItem) => {
                const activityRowCount = activityItem.rows.length;
                let isFirstRowInActivity = true;

                activityItem.rows.forEach((row) => {
                    // Only render row if both group and COA are expanded
                    if (isGroupExpanded && isCoaExpanded) {
                        allRows.push(
                            <DesktopDataRow
                                key={getRowKey(row)}
                                row={row}
                                showCoaGroup={isFirstRowInGroup}
                                showCoa={isFirstRowInCoa}
                                showActivity={isFirstRowInActivity}
                                coaGroupRowSpan={visibleRowCount}
                                coaRowSpan={coaRowCount}
                                activityRowSpan={activityRowCount}
                                isCoaGroupExpanded={isGroupExpanded}
                                isCoaExpanded={isCoaExpanded}
                                onToggleCoaGroup={() => toggleCoaGroup(group.coaGroup)}
                                onToggleCoa={() => toggleCoa(group.coaGroup, coaItem.coa)}
                            />
                        );
                    } else if (isGroupExpanded && !isCoaExpanded && isFirstRowInCoa) {
                        // Show only the COA header row when collapsed
                        allRows.push(
                            <Tr key={`collapsed-coa-${coaKey}`} _hover={{ bg: 'gray.50' }}>
                                {isFirstRowInGroup && (
                                    <Td
                                        rowSpan={visibleRowCount}
                                        fontSize="xs"
                                        fontWeight="bold"
                                        color="gray.700"
                                        borderRight="1px"
                                        borderColor="gray.200"
                                        py={2}
                                        verticalAlign="top"
                                    >
                                        <HStack spacing={2}>
                                            <IconButton
                                                aria-label="Collapse"
                                                icon={<ChevronDownIcon />}
                                                size="xs"
                                                variant="ghost"
                                                onClick={() => toggleCoaGroup(group.coaGroup)}
                                            />
                                            <Text>{row.coaGroup}</Text>
                                        </HStack>
                                    </Td>
                                )}
                                <Td
                                    fontSize="xs"
                                    fontWeight="semibold"
                                    color="gray.700"
                                    borderRight="1px"
                                    borderColor="gray.200"
                                    py={2}
                                    verticalAlign="top"
                                    colSpan={9}
                                >
                                    <HStack spacing={2}>
                                        <IconButton
                                            aria-label="Expand"
                                            icon={<ChevronRightIcon />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => toggleCoa(group.coaGroup, coaItem.coa)}
                                        />
                                        <Text>{row.coa}</Text>
                                        <Badge colorScheme="gray" fontSize="xs" ml={2}>
                                            {coaItem.activities.length} {coaItem.activities.length === 1 ? 'activity' : 'activities'}
                                        </Badge>
                                    </HStack>
                                </Td>
                            </Tr>
                        );
                    } else if (!isGroupExpanded && isFirstRowInGroup) {
                        // Show only the COA Group header row when collapsed
                        allRows.push(
                            <Tr key={`collapsed-group-${group.coaGroup}`} _hover={{ bg: 'gray.50' }}>
                                <Td
                                    fontSize="xs"
                                    fontWeight="bold"
                                    color="gray.700"
                                    borderRight="1px"
                                    borderColor="gray.200"
                                    py={2}
                                    colSpan={10}
                                >
                                    <HStack spacing={2}>
                                        <IconButton
                                            aria-label="Expand"
                                            icon={<ChevronRightIcon />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={() => toggleCoaGroup(group.coaGroup)}
                                        />
                                        <Text>{row.coaGroup}</Text>
                                        <Badge colorScheme="gray" fontSize="xs" ml={2}>
                                            {group.coaList.length} {group.coaList.length === 1 ? 'category' : 'categories'}
                                        </Badge>
                                    </HStack>
                                </Td>
                            </Tr>
                        );
                    }

                    isFirstRowInGroup = false;
                    isFirstRowInCoa = false;
                    isFirstRowInActivity = false;
                });
            });
        });
    });

    return <>{allRows}</>;
};

/**
 * Table Legend Component
 */
const TableLegend: React.FC = () => (
    <Box p={4} borderTop="1px" borderColor="gray.200" bg="gray.50">
        <HStack spacing={6} flexWrap="wrap">
            <HStack spacing={2}>
                <Box w={4} h={4} bg="orange.200" borderRadius="sm" border="1px" borderColor="orange.400" />
                <Text fontSize="xs" color="gray.600">
                    Budget tidak tersedia
                </Text>
            </HStack>
            <HStack spacing={2}>
                <Box w={4} h={4} bg="yellow.200" borderRadius="sm" border="1px" borderColor="yellow.400" />
                <Text fontSize="xs" color="gray.600">
                    Norma aktual melebihi budget
                </Text>
            </HStack>
        </HStack>
    </Box>
);

/**
 * Loading Skeleton Component
 */
const LoadingSkeleton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
    <Box>
        {isMobile ? (
            <VStack spacing={3}>
                {[1, 2, 3].map((i) => (
                    <Skeleton key={`skeleton-mobile-${i}`} height="300px" borderRadius="xl" width="100%" />
                ))}
            </VStack>
        ) : (
            <Stack spacing={2}>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
                    <Skeleton key={`skeleton-desktop-${i}`} height="40px" borderRadius="md" width="100%" />
                ))}
            </Stack>
        )}
    </Box>
);

/**
 * Empty State Component
 */
const EmptyState: React.FC = () => (
    <Box
        bg="white"
        p={8}
        borderRadius="xl"
        textAlign="center"
        border="1px"
        borderColor="gray.200"
    >
        <Text color="gray.500" fontSize="lg">
            Tidak ada data
        </Text>
        <Text color="gray.400" fontSize="sm" mt={2}>
            Coba ubah filter atau pilih periode lain
        </Text>
    </Box>
);

/**
 * Main Table Component
 */
const UpkeepMonitoringTable: React.FC<UpkeepMonitoringTableProps> = ({ data, loading = false }) => {
    const isMobile = useBreakpointValue({ base: true, xl: false });

    // Transform data to hierarchy
    const hierarchy = useMemo(() => {
        if (!data || data.length === 0) return [];
        return transformToHierarchy(data);
    }, [data]);

    // Loading state
    if (loading) {
        return <LoadingSkeleton isMobile={isMobile ?? false} />;
    }

    // Empty state
    if (!data || data.length === 0 || hierarchy.length === 0) {
        return <EmptyState />;
    }

    // Mobile view - collapsible cards
    if (isMobile) {
        return (
            <VStack spacing={4} align="stretch">
                {hierarchy.map((group) => (
                    <MobileCoaGroupCard
                        key={`mobile-group-${group.coaGroup}`}
                        group={group}
                    />
                ))}
            </VStack>
        );
    }

    // Desktop view - table with collapse/expand functionality
    return (
        <Box
            bg="white"
            borderRadius="xl"
            boxShadow="sm"
            border="1px"
            borderColor="gray.200"
            overflowX="auto"
        >
            <Table variant="simple" size="sm">
                <DesktopTableHeader />
                <Tbody>
                    <DesktopTableBody hierarchy={hierarchy} />
                </Tbody>
            </Table>
            <TableLegend />
        </Box>
    );
};

export default UpkeepMonitoringTable;
